import { useState } from 'react';
import './contact.scss';
import { useTranslation } from "react-i18next";
import { RxEnvelopeClosed } from "react-icons/rx";
import { textareaMaxlength, textareaRows } from '../../utils/constants/forms';
import { EmailService } from '../../services/emailService';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import HeaderInfos from '../commons/header-infos/header-infos';
import type { ErrorContactForm } from '../../interfaces/error';
import { LoaderSpinner } from '../commons/loader-spinner/loader-spinner';
import { SEO } from '../commons/seo/seo';

export function Contact(){

    const { t } = useTranslation();
    const [email,setEmail] = useState('');
    const [subject,setSubject] = useState('');
    const [message,setMessage] = useState('');
    const [errors,setErrors] = useState<ErrorContactForm>({});
    const emailService = EmailService();
    const [loading, setLoading] = useState(false);
    const [visitorReference, setVisitorReference] = useState('');
    const [formLoadedAt] = useState(Date.now());


    function handleChangeEmail(
        e: React.ChangeEvent<HTMLInputElement>
    ) {
    
        const value = DOMPurify.sanitize(e.target.value, {
            ALLOWED_TAGS: []
        });
    
        setEmail(value);
    
        if (e.target.validity.valid) {
    
            setErrors(previous => ({
                ...previous,
                mail: ''
            }));
    
        } else {
    
            setErrors(previous => ({
                ...previous,
                mail: "contact.invalid_email_format"
            }));
        }
    }

    function handleChangeSubject(e: React.ChangeEvent<HTMLSelectElement>){
        const value = DOMPurify.sanitize(e.target.value, {ALLOWED_TAGS: []})

        if (value.trim() !== '') {

            setSubject(value);
            setErrors({ ...errors,subject:''});

        }else{

            setErrors({...errors, subject:"contact.please_specify_request_subject"});
        }
    }

    function handleChangeMessage (e: React.ChangeEvent<HTMLTextAreaElement>){
        const value = DOMPurify.sanitize(e.target.value, {ALLOWED_TAGS: []});
        if (value.trim() !== '') {
            setMessage(value);
            setErrors({...errors, message: ''});
        }else {
            setErrors({...errors, message:  "contact.please_seize_message"});
        }
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>){
        e.preventDefault();
        if (!email || !subject || !message) {
            setErrors({
                mail: !email ? "contact.invalid_email_format" : '',
                subject: !subject ? "contact.please_specify_request_subject" : '',
                message: !message ? "contact.please_seize_message" : '',

            });
            return;
        }
        setLoading(true);

        const post = {
            service_id: import.meta.env.VITE_EMAIL_LIST_SERVICE_ID,
            template_id: import.meta.env.VITE_EMAIL_LIST_TEMPLATE_ID,
            user_id: import.meta.env.VITE_EMAIL_LIST_API_PUBLIC_KEY,
            template_params: {  
                from_email: email,
                subject: subject,
                message: message
            } 
        }
        if (visitorReference.trim() !== '') {
            return;
        }

        if (Date.now() - formLoadedAt < 3000) {
            return;
        }

        emailService.sendForm(post)
        .then(() => toast.success(t("contact.message_sent_success")))
        .catch((err) => {
            toast.error(t('contact.mail_send_error'));
            console.error(err);
        }).finally(()=>{
            handleReset();
            setLoading(false);
        });
    }

    function handleReset() {
        setEmail('');
        setSubject('');
        setMessage('');
        setErrors({});
    }

    return (
        <div className='reviews-container content-container'>
            <SEO
            title="Contact — Les Toiles de la Juine"
            description="Contactez Les Toiles de la Juine pour toute question concernant l’exposition artistique consacrée au patrimoine médiéval."
            />
            {loading ? (
            <LoaderSpinner visible={true} />
            ) : (
            <>
            <section className='section-container'>
                <HeaderInfos title={t("contact.label")} description={t("contact.description")}/>
                <div>
                    <h1 className='page-title'>{t("contact.label")}</h1>
                </div>
                <div className ="contact-container">
                    <aside className='padding'>
                        <div className="contact-infos padding-0-15">
                            <a><RxEnvelopeClosed/> contact@lestoilesdelajuine.fr</a>
                        </div>
                        <div className="contact-rs">
                            
                        </div>
                    </aside>
                    <form className='contact-form padding' onSubmit={handleSubmit} method='post'>
                        <div className='contact-form-body'>
                            <label className='desktop-only'>{t("contact.email")}*</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={handleChangeEmail}
                                placeholder={t("contact.email")}
                                className="form-control"
                            /> 
                            <div></div><p className='msg-error'>{errors.mail!=""?t(errors?.mail||''):''}</p>
                            <label>{t("contact.subject")}*</label>
                            <select required className='form-control' onChange = {handleChangeSubject} value={subject}>
                                <option disabled value=""> {t("contact.please_specify_subject")}</option>
                                <option>Webmaster</option>
                                <option>Contacter Les toiles de la Juine</option>
                            </select>
                            <div></div><p className='msg-error'>{errors.subject?t(errors.subject ||''):''}</p>
                            <label className='desktop-only'>{t("contact.message")}*</label>
                            <textarea  required placeholder={t("contact.message")} onChange = {handleChangeMessage} className='form-control' rows={textareaRows} maxLength={textareaMaxlength}/>
                            <div></div><p className='msg-error'>{t(errors.message||'')}</p>
                        </div>
                        {Object.values(errors)}

                        <div className='contact-form-footer padding-15-0'>
                            <div className='honeypot' aria-hidden="true">
                                <input
                                    type="text"
                                    name="visitorReference"
                                    value={visitorReference}
                                    onChange={(e) => setVisitorReference(e.target.value)}
                                    autoComplete="off"
                                    tabIndex={-1}
                                />
                            </div>
                            <button type='submit' >{t("contact.submit")}</button>
                            <button type="button" onClick={handleReset}>{t("contact.reset")}</button>
                        </div>
                    </form>
                </div>
            </section>

            </>)}
        </div>
    )
}
